/**
 * The home page while a release is being prepared. Modal by phase (DESIGN §10):
 * it replaces the idle dashboard with the guided preparation flow — what phase
 * the release is in and what to do about it, the one action that does it, the
 * progress stepper, the change set to cross-check what is shipping (this is the
 * step where that panel earns its keep: there is not yet a per-component table),
 * the announcement editor (the review gate), and the escape hatches. Instruction
 * history lives on the host pages rather than here; the banners above already
 * surface whatever is in flight or just failed. Every action's availability comes
 * from the shared legality table for the current phase, so the UI cannot offer
 * something the host would reject.
 *
 * The primary action is derived, not hard-coded. `prepare.start` runs merge,
 * capture and describe back to back, so this page was written on the assumption
 * that it would only ever be *seen* at `reviewing` and the earlier phases needed
 * no action of their own. When one of the three does not land, that assumption
 * leaves an operator on a page with nothing to press: v1.1264 sat in `capturing`
 * for an hour whose only enabled button was "Re-run describe", hidden in a
 * collapsed accordion, and the wrong step besides. So the page asks
 * `nextPrepareStep` what is outstanding and puts exactly that in front of them
 * (`lib/prepareGuidance.ts`).
 *
 * This view is only reached with the release system awake: `HomePage` replaces the
 * whole flow with the sleeping panel otherwise, because every action here is refused
 * while the machine that runs them is down and a page of dead buttons is what sent
 * v1.1266's operator looking for a tool that had lost track of its own release. So
 * nothing here mentions the machines — waking them is not one of the preparation's
 * steps.
 */
import { useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    AlertTitle,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    INSTRUCTION_KIND,
    PREPARE_ONLY_KINDS,
    PREPARE_STEP_NARRATION,
    componentReleaseSummary,
    hostInstance,
    legalInstructions,
    narrationFor,
    prepareStepLegality,
    untaggedVersion,
    type InstructionKind,
    type Phase,
    type PrepareComponentRelease,
    type PrepareStep,
} from '@finvari/deployer';
import type { PrepareRelease } from '../lib/homeMode';
import { createInstruction } from '../lib/createInstruction';
import { describeActivity, inflightRows, lastOutcomeToReport, type ActivityHost } from '../lib/instructionActivity';
import { hostView } from '../lib/hostStatus';
import {
    capturedSummary,
    newCommitsOnMain,
    nextPrepareStep,
    phaseGuidance,
    type NextPrepareAction,
} from '../lib/prepareGuidance';
import { dbForRole } from '../firebase';
import {
    CAPTION_TICK_MS,
    LIVE_TICK_MS,
    useHost,
    useInstructionAction,
    useNow,
    useRecentInstructions,
    useUnreleased,
} from '../hooks';
import { useOperator } from '../auth';
import { StepperView } from './StepperView';
import { ChangeSetView } from './ChangeSetView';
import { AnnouncementEditor } from './AnnouncementEditor';
import { ActionCard } from './ActionCard';
import { ActivityAlert } from './ActivityAlert';

/** Why the step buttons are held while the host is busy. */
const BUSY_REASON = 'An instruction is already running on the prepare host — wait for it to finish.';

/** Human phase labels for the header chip. */
const PHASE_LABEL: Partial<Record<Phase, string>> = {
    prepare_host_starting: 'Waiting for the system',
    merging: 'Merging main',
    capturing: 'Capturing PRs',
    describing: 'Drafting announcement',
    reviewing: 'Awaiting review',
    publishing: 'Publishing',
};

export function PreparingView({ release, phase }: { release: PrepareRelease; phase: Phase }) {
    // The stepper only moves when the host re-projects the release doc, which is
    // after an instruction finishes; between clicks and that write this banner is
    // the only thing that says the host is working.
    const prepareHost = useHost('prepare');
    const recent = useRecentInstructions('prepare').value;
    const unreleased = useUnreleased();
    const inflight = inflightRows(recent);
    // A running step shows "Running for 1m 20s" and wants the second hand; with
    // nothing in flight the page's only clock-derived text is a coarse "2m ago".
    const now = useNow(inflight.length > 0 ? LIVE_TICK_MS : CAPTION_TICK_MS);
    // The stepper only knows done/next/pending, so a rejected or failed attempt
    // leaves it looking as if the step is simply up next; this is where the
    // operator learns that the last thing they asked for did not happen, and why.
    const lastOutcome = lastOutcomeToReport(recent, PREPARE_ONLY_KINDS, now);
    const activityHost: ActivityHost = {
        role: 'prepare',
        view: hostView(prepareHost.value, now, prepareHost.loading),
        expectedBootSeconds: hostInstance('prepare')?.expectedBootSeconds,
    };
    const busyReason = inflight.length > 0 ? BUSY_REASON : undefined;

    const guidance = phaseGuidance(phase);
    const nextAction = nextPrepareStep(release.steps, phase);
    const newMain = newCommitsOnMain(unreleased.value, prepareHost.value);
    // Describe's own status, not the announcement field: a re-run merge deletes the
    // draft on disk but the release document keeps the last one it published, so an
    // editor keyed on the field alone would offer a draft of a branch that no
    // longer exists for review and approval.
    const announcementJson = release.steps?.describe === 'done' ? release.announcement?.json : undefined;
    // A release cut before this field existed has none, and the page shows
    // whichever release is newest — including, on an idle system, an old one.
    const componentReleases = release.componentReleases ?? [];

    return (
        <Stack spacing={3}>
            <Stack spacing={1}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="h5">Preparing {release.version}</Typography>
                    <Chip color="primary" label={PHASE_LABEL[phase] ?? phase} />
                </Stack>
                <Typography variant="subtitle1">{guidance.headline}</Typography>
                <Typography variant="body2" color="text.secondary">
                    {guidance.detail}
                </Typography>
            </Stack>

            {inflight.length > 0 && (
                <Stack spacing={1}>
                    {inflight.map((row) => (
                        <ActivityAlert
                            key={row.id}
                            host="prepare"
                            row={row}
                            line={describeActivity(row, activityHost, now)}
                            label={narrationFor(row.kind).title}
                        />
                    ))}
                </Stack>
            )}

            {lastOutcome && (
                <ActivityAlert
                    host="prepare"
                    row={lastOutcome}
                    line={describeActivity(lastOutcome, activityHost, now)}
                    label={narrationFor(lastOutcome.kind).title}
                />
            )}

            {(release.attention?.length ?? 0) > 0 && (
                <Stack spacing={1}>
                    {release.attention.map((a) => (
                        <Alert key={a.code} severity="warning">
                            <strong>{a.code}:</strong> {a.message}
                        </Alert>
                    ))}
                </Stack>
            )}

            {/* The page's one primary action. Held while the host is busy: a second
                click only queues a duplicate of a step that is already running. */}
            {!busyReason && <NextStepCard action={nextAction} phase={phase} version={release.version} />}

            <StepperView steps={release.steps} />

            <NewCommitsCard
                phase={phase}
                version={release.version}
                newMain={newMain}
                busyReason={busyReason}
                unreleasedLoading={unreleased.loading}
            />

            <Card variant="outlined">
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        What is shipping
                    </Typography>
                    {release.changes ? (
                        <>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                {capturedSummary(release)}
                            </Typography>
                            <ComponentReleaseList releases={componentReleases} />
                            <ChangeSetView pointer={release.changes} />
                        </>
                    ) : (
                        // The change set is capture's output for the branch capture ran
                        // against, and a merge invalidates it. Saying so beats showing the
                        // previous capture's file list as though it were this release's.
                        <Typography variant="body2" color="text.secondary">
                            Not captured yet for the current release branch. The capture step works out what is in the
                            release and lists it here.
                        </Typography>
                    )}
                </CardContent>
            </Card>

            {/*
              * The component announcements come first because they are the ones an
              * operator does not expect: a release carries a connector build a few
              * times a year, and the review gate is the only place it can be caught
              * before it goes out. The release's own announcement is below, with the
              * Approve button that publishes all of them.
              */}
            {componentReleases.map((componentRelease) => (
                <Card key={`${componentRelease.component}/${componentRelease.version}`} variant="outlined">
                    <CardContent>
                        <AnnouncementEditor
                            version={release.version}
                            phase={phase}
                            json={
                                release.steps?.describe === 'done' ? componentRelease.announcement?.json : undefined
                            }
                            actionsBlockedReason={busyReason}
                            target={{ component: componentRelease.component, version: componentRelease.version }}
                            title={`${componentRelease.component === 'app' ? 'Phone app' : 'Connector'} ${untaggedVersion(componentRelease.version)} announcement`}
                            subtitle={componentReleaseSummary(componentRelease)}
                        />
                    </CardContent>
                </Card>
            ))}

            <Card variant="outlined">
                <CardContent>
                    <AnnouncementEditor
                        version={release.version}
                        phase={phase}
                        json={announcementJson}
                        actionsBlockedReason={busyReason}
                    />
                </CardContent>
            </Card>

            <Accordion disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>Advanced: re-run a single step or abort</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Stack spacing={2}>
                        <Typography variant="body2" color="text.secondary">
                            The guided action above is the step the release is actually up to; these run one step on its
                            own. Each is idempotent — the host recomputes it from git and files — but a step is only
                            offered from the phase it belongs to onwards, because running one early works on inputs the
                            step before it has not produced yet.
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <RerunStep phase={phase} step="merge" version={release.version} blockedReason={busyReason} />
                            <RerunStep phase={phase} step="capture" version={release.version} blockedReason={busyReason} />
                            <RerunStep phase={phase} step="describe" version={release.version} blockedReason={busyReason} />
                        </Stack>
                        <Divider />
                        <ActionCard
                            host="prepare"
                            kind={INSTRUCTION_KIND.prepareAbort}
                            release={release.version}
                            disabledReason={reasonFor(phase, INSTRUCTION_KIND.prepareAbort) ?? busyReason}
                        />
                    </Stack>
                </AccordionDetails>
            </Accordion>
        </Stack>
    );
}

/** The operator-facing reason a prepare action is unavailable in this phase, if any. */
function reasonFor(phase: Phase, kind: InstructionKind): string | undefined {
    return legalInstructions(phase, 'prepare').find((l) => l.kind === kind)?.reasonIfIllegal;
}

/**
 * The connector and app releases riding this one, above the file list.
 *
 * Given its own block rather than a line in the caption because it is the part
 * of "what is shipping" an operator is least expecting. A connector build
 * happens a few times a year and arrives as an ordinary merged pull request; if
 * the only sign of it were a file in the change set, the release would announce
 * it without anyone having decided to.
 */
function ComponentReleaseList({ releases }: { releases: PrepareComponentRelease[] }) {
    if (releases.length === 0) return null;
    return (
        <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>This release also releases {releases.length === 1 ? 'a component' : 'components'}</AlertTitle>
            <Stack spacing={1}>
                {releases.map((r) => (
                    <Stack key={`${r.component}/${r.version}`} spacing={0.25}>
                        <Typography variant="body2">{componentReleaseSummary(r)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {r.pullRequests.length === 0
                                ? 'No pull requests of its own — nothing in it changed.'
                                : `${r.pullRequests.length} PRs, ${r.promotedFromPending} of them waiting since the last ${r.component} release. They get their own announcement below.`}
                        </Typography>
                    </Stack>
                ))}
            </Stack>
        </Alert>
    );
}

/**
 * The one thing to do next. A step gets the emphasised card that runs it, with
 * that step's own narration rather than `prepare.step`'s (which has to describe
 * all five at once); review gets a pointer at the editor further down, because
 * the action there is reading, and the buttons that end it live with the draft.
 */
function NextStepCard({
    action,
    phase,
    version,
}: {
    action: NextPrepareAction;
    phase: Phase;
    version: string;
}) {
    if (!action) return null;
    // `nextPrepareStep` still answers `start-host` for a stopped system, and the
    // debug snapshot reports it, but this page is not where that is acted on:
    // `HomePage` shows the sleeping panel instead of the flow, so reaching here with
    // that answer means the page is a render behind the heartbeat.
    if (action === 'start-host') return null;
    if (action === 'review') {
        return (
            <Alert severity="info">
                <AlertTitle>The announcement is drafted and waiting for you</AlertTitle>
                Read it in the editor below, edit anything that reads wrong, then approve. Approving publishes{' '}
                {version} and pushes its tag.
            </Alert>
        );
    }
    return (
        <ActionCard
            host="prepare"
            kind={INSTRUCTION_KIND.prepareStep}
            params={{ step: action }}
            release={version}
            narration={PREPARE_STEP_NARRATION[action]}
            emphasis
            disabledReason={prepareStepLegality(action, phase)}
            // On this step's own instructions, not the newest of the kind: a rerun
            // button pressed earlier would otherwise report its outcome here.
            match={(row) => (row.params as { step?: string } | undefined)?.step === action}
        />
    );
}

/**
 * Commits that landed on main after the release was cut.
 *
 * Its own card because it is the one thing a preparing operator has to decide
 * rather than execute: fold the new work in, or ship without it. The action that
 * folds it in is a single instruction — `prepare.include-new-main` — because
 * merge alone deletes the capture and the announcement, and an operator who
 * pressed only that is left with a release nothing on the page can describe.
 *
 * When there is no current answer the card offers the check instead. Every
 * successful merge moves the release branch and so invalidates the last one, and
 * a count taken before the merge would report the operator's own work back to
 * them as new.
 */
function NewCommitsCard({
    phase,
    version,
    newMain,
    busyReason,
    unreleasedLoading,
}: {
    phase: Phase;
    version: string;
    newMain: ReturnType<typeof newCommitsOnMain>;
    busyReason?: string;
    unreleasedLoading: boolean;
}) {
    const illegal = reasonFor(phase, INSTRUCTION_KIND.prepareIncludeNewMain);
    // Illegal here means publishing: the tag is being pushed, and what lands on
    // main now belongs to the next release. Nothing to offer and nothing to check.
    if (illegal || unreleasedLoading) return null;

    if (newMain.ahead) {
        return (
            <ActionCard
                host="prepare"
                kind={INSTRUCTION_KIND.prepareIncludeNewMain}
                release={version}
                title={`Include ${newMain.commits} new commit${newMain.commits === 1 ? '' : 's'} from main`}
                disabledReason={busyReason}
            />
        );
    }

    return (
        <Card variant="outlined">
            <CardContent>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap">
                    <Stack sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2">New commits on main</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {newMain.unchecked
                                ? 'Nobody has looked since this release was cut. Anything merged to main after the cut can still be folded in, up until publish.'
                                : newMain.stale
                                  ? 'The last check describes a release branch this host has since moved off, so it cannot say. Check again to find out.'
                                  : 'Nothing has landed on main since the release branch was merged, as of the last check.'}
                        </Typography>
                    </Stack>
                    <CheckMainButton blockedReason={busyReason} />
                </Stack>
            </CardContent>
        </Card>
    );
}

/** `host.sync` under the name of the question it answers here. */
function CheckMainButton({ blockedReason }: { blockedReason?: string }) {
    const { run, sending, error, disabledReason } = useInstructionAction('prepare', INSTRUCTION_KIND.hostSync, {
        disabledReason: blockedReason,
    });
    const button = (
        <Button
            size="small"
            variant="outlined"
            onClick={run}
            disabled={sending || !!disabledReason}
            startIcon={sending ? <CircularProgress size={14} color="inherit" /> : undefined}
            sx={{ flexShrink: 0 }}
        >
            Check main for new commits
        </Button>
    );
    return (
        <Stack sx={{ flexShrink: 0 }}>
            {disabledReason ? (
                <Tooltip title={disabledReason}>
                    <span>{button}</span>
                </Tooltip>
            ) : (
                button
            )}
            {error && (
                <Typography variant="caption" color="error">
                    {error}
                </Typography>
            )}
        </Stack>
    );
}

function RerunStep({
    phase,
    step,
    version,
    blockedReason,
}: {
    phase: Phase;
    step: PrepareStep;
    version: string;
    /** Set while another instruction is in flight on the host. */
    blockedReason?: string;
}) {
    const operator = useOperator();
    const [error, setError] = useState<string | undefined>(undefined);
    // Per step, not per kind: the steps become legal in the order they run, and
    // merge-back is legal in phases the others are not.
    const reason = prepareStepLegality(step, phase) ?? blockedReason;
    const run = async () => {
        setError(undefined);
        const result = await createInstruction(
            dbForRole('prepare'),
            'prepare',
            INSTRUCTION_KIND.prepareStep,
            { step },
            operator,
            version,
        );
        if (!result.ok) setError(result.error);
    };
    return (
        <Tooltip title={reason ?? `Re-run ${step}`}>
            <span>
                <Button size="small" variant="outlined" disabled={!!reason} onClick={() => void run()}>
                    Re-run {step}
                </Button>
                {error && (
                    <Typography variant="caption" color="error" display="block">
                        {error}
                    </Typography>
                )}
            </span>
        </Tooltip>
    );
}
